import { useCallback, useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { getListProduct } from "../../../../services";
import { ProductListItem } from "../../../../types/product.type";
import { numberWithCommas } from "../../../../utils";
import Skeleton from "../../../ReprintAuth/Skeleton";
import CataloguePagination from "../../shared/Pagination";
import { useSearchablePagedRows } from "../../shared/useSearchablePagedRows";
import {
  CATALOGUE_CELL_PADDING,
  STRIPE_BACKGROUND,
  catalogueRowHeight,
} from "../../shared/constants";

const THUMBNAIL_SIZE = 36;
/** The thumbnail is the tallest thing in a row, so it sets the row height. */
const ROW_HEIGHT = catalogueRowHeight(THUMBNAIL_SIZE);

const COLUMNS = ["รหัสสินค้า", "ชื่อสินค้า"];
/** The checkbox column carries no header label, so it is counted on its own. */
const COLUMN_COUNT = COLUMNS.length + 1;

const ALREADY_ADDED_HINT = "อยู่ในสมุดราคาแล้ว";

interface AddProductsModalProps {
  open: boolean;
  /** Products the pricebook already holds; they may be shown but never added twice. */
  addedProductCodes: Set<string>;
  onClose: () => void;
  onConfirm: (products: ProductListItem[]) => void;
}

/**
 * Picks products out of the catalogue to put on the pricebook. The สินค้า tab itself never
 * reads the catalogue — it only holds the lines of the pricebook being edited.
 */
export default function AddProductsModal({
  open,
  addedProductCodes,
  onClose,
  onConfirm,
}: AddProductsModalProps) {
  // Keyed by product code so a pick survives the page or the search term it was made
  // on: the confirm sends every checked product, not just the visible ones.
  const [checkedProducts, setCheckedProducts] = useState<
    Record<string, ProductListItem>
  >({});

  const fetchProducts = useCallback(
    async (page: number, limit: number, search?: string) => {
      const {
        data: { data },
      } = await getListProduct(page, limit, { search });
      const rows: ProductListItem[] = data.rows;
      const count: number = data.count;
      return { rows, count };
    },
    [],
  );

  const {
    search,
    handleChangeSearch,
    page,
    setPage,
    limit,
    setLimit,
    rows: products,
    totalCount,
    loading,
  } = useSearchablePagedRows({ open, fetchRows: fetchProducts });

  // Reopening the picker should start empty rather than resume the last session's picks.
  useEffect(() => {
    if (open) return;
    setCheckedProducts({});
  }, [open]);

  const handleToggleProduct = (product: ProductListItem) => {
    setCheckedProducts((previous) => {
      const next = { ...previous };
      if (next[product.code]) {
        delete next[product.code];
      } else {
        next[product.code] = product;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Object.values(checkedProducts));
  };

  const checkedCount = Object.keys(checkedProducts).length;
  const confirmLabel = `เพิ่ม ${numberWithCommas(checkedCount)} รายการ`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        component="div"
        sx={{ display: "flex", alignItems: "center", gap: 2 }}
      >
        <Typography variant="body1" fontWeight={600} sx={{ flexGrow: 1 }}>
          เพิ่มสินค้า{" "}
          <Typography component="span" variant="body2" color="text.secondary">
            (เลือกแล้ว {numberWithCommas(checkedCount)} รายการ)
          </Typography>
        </Typography>
        <IconButton size="small" aria-label="ปิด" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack gap={2}>
          <TextField
            size="small"
            fullWidth
            placeholder="ค้นหารหัสสินค้า หรือชื่อสินค้า"
            value={search}
            onChange={handleChangeSearch}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <TableContainer>
            <Table>
              <TableHead
                sx={{ bgcolor: (theme) => theme.palette.background.default }}
              >
                <TableRow>
                  <TableCell padding="checkbox" />
                  {COLUMNS.map((column) => (
                    <TableCell key={column}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <Skeleton
                    columns={COLUMN_COUNT}
                    rows={limit}
                    rowHeight={ROW_HEIGHT}
                  />
                )}
                {!loading && products.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={COLUMN_COUNT}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <Typography color="text.secondary" fontWeight={500}>
                        ไม่พบข้อมูลสินค้า
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  products.map((product, index) => (
                    <ProductPickerRow
                      key={product.uuid}
                      product={product}
                      striped={index % 2 === 1}
                      checked={Boolean(checkedProducts[product.code])}
                      alreadyAdded={addedProductCodes.has(product.code)}
                      onToggle={handleToggleProduct}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <CataloguePagination
            totalCount={totalCount}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button
          variant="contained"
          disabled={checkedCount === 0}
          onClick={handleConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ProductPickerRowProps {
  product: ProductListItem;
  striped: boolean;
  checked: boolean;
  /** The pricebook already holds this product, so it can only be shown, not picked. */
  alreadyAdded: boolean;
  onToggle: (product: ProductListItem) => void;
}

function ProductPickerRow({
  product,
  striped,
  checked,
  alreadyAdded,
  onToggle,
}: ProductPickerRowProps) {
  const handleToggle = () => {
    onToggle(product);
  };

  return (
    <TableRow
      sx={{
        bgcolor: (theme) =>
          striped ? STRIPE_BACKGROUND : theme.palette.background.paper,
        height: ROW_HEIGHT,
        "& td, & th": { padding: `${CATALOGUE_CELL_PADDING}px` },
      }}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={checked || alreadyAdded}
          disabled={alreadyAdded}
          onChange={handleToggle}
          slotProps={{ input: { "aria-label": `เลือก ${product.name}` } }}
        />
      </TableCell>
      <TableCell>{product.code}</TableCell>
      <TableCell>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{ minWidth: 0 }}
        >
          <Avatar
            src={product.thumbnail ?? undefined}
            alt={product.name}
            variant="rounded"
            sx={{
              width: THUMBNAIL_SIZE,
              height: THUMBNAIL_SIZE,
              flexShrink: 0,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {product.name}
            </Typography>
            {alreadyAdded && (
              <Typography variant="caption" color="text.secondary">
                {ALREADY_ADDED_HINT}
              </Typography>
            )}
          </Box>
        </Stack>
      </TableCell>
    </TableRow>
  );
}
